import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	useController,
	useForm,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetFieldOptionRegionDetailQuery,
	useGetFieldOptionRegionListQuery,
	useUpdateFieldOptionRegionDetailOrderMutation,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import DndArticleListSection from './components/re-order/DndArticleListSection';
import { arrayMove } from '@dnd-kit/sortable';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
	title?: string;
	type?: number;
}
export default function RegionOrderPage(props: Props) {
	const { title, type } = props;
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { fieldId, districtId, subDistrictId, ecvId, buildId } = useParams();
	const [updateFieldOptionRegionDetailOrder] =
		useUpdateFieldOptionRegionDetailOrderMutation();

	const { data: districtDetails } = useGetFieldOptionRegionDetailQuery(
		districtId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 1 && !districtId,
		}
	);
	const { data: subDistrictDetails } = useGetFieldOptionRegionDetailQuery(
		subDistrictId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 2 && !subDistrictId,
		}
	);
	const { data: ecvDetails } = useGetFieldOptionRegionDetailQuery(ecvId || '', {
		refetchOnMountOrArgChange: true,
		skip: type !== 3 && !ecvId,
	});
	const { data: buildDetails } = useGetFieldOptionRegionDetailQuery(
		buildId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 4 && !buildId,
		}
	);

	const [meditationItems, setMeditationItems] = useState<any>([]);

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionRegionListQuery(
			{
				pagination: { page: 1, size: 99999 },
				searchString: '',
				sortings: [],
				type: type ?? 0,
				parentId:
					type === 1
						? districtId
						: type === 2
						? subDistrictId
						: type === 3
						? ecvId
						: type === 4
						? buildId
						: '',
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	useEffect(() => {
		if (propertySummary) {
			if (propertySummary?.data?.items) {
				setMeditationItems(propertySummary?.data?.items || []);
			}
		}
	}, [propertySummary]);

	const handleRepositionItem = (activeIndex: number, overIndex: number) => {
		setMeditationItems((prev: unknown[]) =>
			arrayMove(prev, activeIndex, overIndex)
		);
	};

	const handleSubmit = async () => {
		const response = await updateFieldOptionRegionDetailOrder(
			(meditationItems || []).map((item: any) => item.id)
		);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFieledUnitOrderSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				type === 1
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}`
					: type === 2
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}`
					: type === 3
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}`
					: type === 4
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/build/${buildId}`
					: `/field-management/field-management-list/region/${fieldId}`
			);
		}
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{type === 1
						? `${t('field.manageStatus')} (District (${
								districtDetails?.data.nameEn
						  }))`
						: type === 2
						? `${t('field.manageStatus')} (Sub-district (${
								subDistrictDetails?.data.nameEn
						  }))`
						: type === 3
						? `${t('field.manageStatus')} (Estate / Court / Village (${
								ecvDetails?.data.nameEn
						  }))`
						: type === 4
						? `${t('field.manageStatus')} (Building / Block (${
								buildDetails?.data.nameEn
						  }))`
						: t('drawerListItems.regionStatus')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<DndArticleListSection
								meditationItems={transformResponse(meditationItems) ?? []}
								handleRepositionItem={handleRepositionItem}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					onClick={() => {
						navigate(
							type === 1
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}`
								: type === 2
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}`
								: type === 3
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}`
								: type === 4
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/build/${buildId}`
								: `/field-management/field-management-list/region/${fieldId}`
						);
					}}
				>
					{t('button.cancel')}
				</Button>
				<Button type='submit' variant='contained' onClick={handleSubmit}>
					{t('button.complete')}
				</Button>
			</Box>
		</>
	);
}
